import { REALTIME } from "../config/global.configuration";
import { buildTimeline } from "../engine/TimelineBuilder";
import type {
    BBox,
    SnapshotResponse,
    TripDelayWire,
    TripDetailResponse,
    TripIndexEntry,
    TripScheduleWire,
} from "../types/dtos/realtime-api/realtime.dto";
import { coercePointsBin } from "./realtime/coercePointsBin";
import { CompactShape } from "./realtime/CompactShape";
import { type CompactTimeline } from "./realtime/CompactTimeline";

interface TimedEntry<T> {
    "data":     T;
    "lastUsed": number;
}

/**
 * Frontend cache for realtime transit data.
 * Maintains 5 caches: tripIndex, shapes, schedules, timelines, delays.
 * Shapes are deduplicated and shared across trips (~7:1 ratio).
 *
 * Shape geometry is stored only as {@link CompactShape} (never wire POJOs or `points_bin` bytes).
 */
export class RealtimeCache {

    tripIndex: Map<string, TripIndexEntry> = new Map();

    shapes: Map<string, TimedEntry<CompactShape>> = new Map();

    schedules: Map<string, TimedEntry<TripScheduleWire>> = new Map();

    timelines: Map<string, CompactTimeline> = new Map();

    delays: Map<string, TripDelayWire> = new Map();

    knownShapeIds: Set<string> = new Set();

    knownTripIds: Set<string> = new Set();

    /**
     * Load the trip index from /init response.
     *
     * @param entries Trip index entries returned by the init endpoint.
     * @returns Nothing.
     */
    loadTripIndex( entries: TripIndexEntry[] ): void {
        this.tripIndex.clear();
        for ( const entry of entries ) {
            this.tripIndex.set( entry.trip_id, entry );
        }
    }

    /**
     * Apply a snapshot response: cache new shapes, rebuild timelines, update delays.
     *
     * **No wire retention:** each `new_shapes[i]` is converted to {@link CompactShape} in this
     * loop; `points_bin` bytes and the snapshot object are not stored. {@link PollingManager}
     * does not keep the HTTP response after `applySnapshot` returns.
     *
     * `removed_trip_ids` from the server are filtered against the **current** viewport (trip
     * index + bbox at apply time) so a stale snapshot cannot drop trips still on screen.
     *
     * @param snapshot Snapshot payload containing deltas for shapes, trips, and delays.
     * @param viewport Current expanded map bounds and time used to validate removals.
     * @returns Nothing.
     */
    applySnapshot(
        snapshot: SnapshotResponse,
        viewport: {
            "bbox":          BBox;
            "currentTimeMs": number;
        },
    ): void {
        const now = Date.now();

        // Shapes: coerce points_bin → CompactShape immediately (never cache wire POJOs or Uint8Array).
        for ( const shape of snapshot.new_shapes ) {
            const pointsBin = coercePointsBin(
                shape.points_bin,
                `Snapshot shape ${ shape.shape_id }`,
            );
            const compact = CompactShape.fromBuffer( pointsBin );
            this.shapes.set( shape.shape_id, {
                "data":     compact,
                "lastUsed": now,
            } );
            this.knownShapeIds.add( shape.shape_id );
        }

        // 2. Cache new schedules and rebuild timelines
        for ( const trip of snapshot.new_trips ) {
            this.schedules.set( trip.trip_id, {
                "data":     trip,
                "lastUsed": now,
            } );
            this.knownTripIds.add( trip.trip_id );

            const shapeEntry = this.shapes.get( trip.shape_id );
            if ( shapeEntry ) {
                shapeEntry.lastUsed = now;
                const timeline = buildTimeline( shapeEntry.data, trip );
                this.timelines.set( trip.trip_id, timeline );
            }
        }

        /*
         * 3. Remove trips that left the viewport.
         * Do not apply server `removed_trip_ids` blindly: stale snapshots (old bbox) otherwise
         * delete timelines for buses still on screen → visible flicker. Re-filter against the
         * current expanded viewport before evicting. Do not "optimize" this away for memory.
         */
        const stillVisibleTripIds = new Set( this.getVisibleTrips( viewport.bbox, viewport.currentTimeMs ) );
        for ( const tripId of snapshot.removed_trip_ids ) {
            if ( stillVisibleTripIds.has( tripId ) ) {
                continue;
            }
            this.timelines.delete( tripId );
            this.schedules.delete( tripId );
            this.knownTripIds.delete( tripId );
        }

        // 4. Update delays (replace all)
        this.delays.clear();
        for ( const d of snapshot.delays ) {
            this.delays.set( d.trip_id, d );
        }
    }

    /**
     * Hydrates a single trip from `/trip/{id}` detail (shape + schedule + timeline).
     *
     * Used for programmatic selection of trips outside the polled viewport (itinerary
     * vehicle auto-select): `knownTripIds`/`knownShapeIds` are marked so {@link evictStale}
     * keeps the entry, and server `removed_trip_ids` never references it.
     * Stop `name` is not part of the detail payload; a later viewport snapshot refreshes it.
     *
     * @param detail Trip detail payload.
     * @returns `true` when a usable timeline was built, `false` otherwise.
     */
    upsertTripDetail( detail: TripDetailResponse ): boolean {
        const now = Date.now();

        const pointsBin = coercePointsBin(
            detail.shape.points_bin,
            `Trip detail shape ${ detail.shape_id }`,
        );
        const compact = CompactShape.fromBuffer( pointsBin );
        this.shapes.set( detail.shape_id, {
            "data":     compact,
            "lastUsed": now,
        } );
        this.knownShapeIds.add( detail.shape_id );

        const schedule: TripScheduleWire = {
            "trip_id":                detail.trip_id,
            "shape_id":               detail.shape_id,
            "route_short_name":       detail.route_short_name,
            "route_color":            detail.route_color,
            "route_type":             detail.route_type,
            "route_type_france":      detail.route_type_france,
            "trip_headsign":          detail.trip_headsign,
            "trip_headsign_original": detail.trip_headsign_original,
            "shape_start_idx":        detail.shape_start_idx,
            "shape_end_idx":          detail.shape_end_idx,
            "stops":                  detail.stops.map( ( stop ) => ( {
                ...stop,
                "name": "",
            } ) ),
        };

        this.schedules.set( detail.trip_id, {
            "data":     schedule,
            "lastUsed": now,
        } );
        this.knownTripIds.add( detail.trip_id );

        const timeline = buildTimeline( compact, schedule );
        if ( timeline.length < 2 ) {
            this.timelines.delete( detail.trip_id );

            return false;
        }

        this.timelines.set( detail.trip_id, timeline );

        return true;
    }

    /**
     * Determine locally which trips are visible given a bbox and current time.
     * Uses the trip index (no backend call needed).
     *
     * @param bbox Current viewport bounds.
     * @param currentTimeMs Current timestamp in milliseconds.
     * @returns Visible trip identifiers for the current viewport and time.
     */
    getVisibleTrips( bbox: BBox, currentTimeMs: number ): string[] {
        const currentSeconds = timestampMsToSecondsSinceMidnight( currentTimeMs );
        const visible: string[] = [];
        const margin = REALTIME.visibleTripMarginS;

        for ( const [ tripId, entry ] of this.tripIndex ) {
            if ( entry.time_end_s < currentSeconds - margin ) {
                continue;
            }
            if ( entry.time_start_s > currentSeconds + margin ) {
                continue;
            }
            if ( entry.bbox[2] < bbox.minLat ) {
                continue;
            }
            if ( entry.bbox[0] > bbox.maxLat ) {
                continue;
            }
            if ( entry.bbox[3] < bbox.minLon ) {
                continue;
            }
            if ( entry.bbox[1] > bbox.maxLon ) {
                continue;
            }
            visible.push( tripId );
        }

        return visible;
    }

    /**
     * Evict stale shapes/schedules/timelines not referenced by any known trip.
     *
     * @returns Nothing.
     */
    evictStale(): void {
        const now = Date.now();

        const usedShapeIds = new Set<string>();
        for ( const [ , schedule ] of this.schedules ) {
            usedShapeIds.add( schedule.data.shape_id );
        }

        for ( const [ shapeId, entry ] of this.shapes ) {
            if ( !usedShapeIds.has( shapeId ) && now - entry.lastUsed > REALTIME.shapeTtlMs ) {
                this.shapes.delete( shapeId );
                this.knownShapeIds.delete( shapeId );
            }
        }

        for ( const [ tripId, entry ] of this.schedules ) {
            if ( !this.knownTripIds.has( tripId ) && now - entry.lastUsed > REALTIME.scheduleTtlMs ) {
                this.schedules.delete( tripId );
                this.timelines.delete( tripId );
            }
        }
    }

    /**
     * Get active trip IDs that have timelines ready for rendering.
     *
     * @returns Trip identifiers with timeline data available.
     */
    getActiveTripIds(): string[] {
        return [ ...this.timelines.keys() ];
    }

    /**
     * Clear all caches.
     *
     * @returns Nothing.
     */
    clear(): void {
        this.tripIndex.clear();
        this.shapes.clear();
        this.schedules.clear();
        this.timelines.clear();
        this.delays.clear();
        this.knownShapeIds.clear();
        this.knownTripIds.clear();
    }

}

/**
 * Converts an epoch timestamp in milliseconds to local seconds since midnight.
 *
 * @param tsMs Epoch timestamp in milliseconds.
 * @returns Local seconds since midnight.
 */
function timestampMsToSecondsSinceMidnight( tsMs: number ): number {
    const d = new Date( tsMs );

    return d.getHours() * 3600 + d.getMinutes() * 60 + d.getSeconds();
}
